import React, { useState, useEffect } from 'react';
import { supabase } from '../services/supabaseClient';
import { Order, Address } from '../types';
import { Package, User, MapPin, LogOut, Edit2, Plus, Trash2, CheckCircle } from 'lucide-react';

interface AccountProps {
    session: any;
    onNavigate: (page: string) => void;
}

export const Account: React.FC<AccountProps> = ({ session, onNavigate }) => {
    const [activeTab, setActiveTab] = useState<'perfil' | 'pedidos' | 'direcciones'>('perfil');
    const [orders, setOrders] = useState<any[]>([]);
    const [isLoading, setIsLoading] = useState(false);

    const [profile, setProfile] = useState<{ full_name: string; phone: string }>({ full_name: '', phone: '' });
    const [addresses, setAddresses] = useState<any[]>([]);
    const [editingAddress, setEditingAddress] = useState<any | null>(null);
    const [message, setMessage] = useState('');

    useEffect(() => {
        if (activeTab === 'pedidos') fetchMyOrders();
        else if (activeTab === 'perfil') fetchProfile();
        else if (activeTab === 'direcciones') fetchAddresses();
    }, [activeTab]);

    const fetchProfile = async () => {
        setIsLoading(true);
        const { data, error } = await supabase.from('users_profiles').select('full_name, phone').eq('id', session?.user?.id).single();
        if (data) setProfile({ full_name: data.full_name || '', phone: data.phone || '' });
        setIsLoading(false);
    };

    const saveProfile = async () => {
        setMessage('');
        const { error } = await supabase.from('users_profiles').update({ full_name: profile.full_name, phone: profile.phone }).eq('id', session?.user?.id);
        if (!error) setMessage('Perfil actualizado con éxito');
        else setMessage('Error actualizando perfil');
        setTimeout(() => setMessage(''), 3000);
    };

    const fetchAddresses = async () => {
        setIsLoading(true);
        const { data } = await supabase.from('addresses').select('*').eq('user_id', session?.user?.id);
        if (data) setAddresses(data);
        setIsLoading(false);
    };

    const saveAddress = async (e: React.FormEvent) => {
        e.preventDefault();
        setMessage('');
        const addrData = {
            user_id: session?.user?.id,
            type: 'shipping',
            full_name: editingAddress.full_name,
            line1: editingAddress.line1,
            city: editingAddress.city,
            postal_code: editingAddress.postal_code,
            country: editingAddress.country,
            phone: editingAddress.phone,
            is_default: editingAddress.is_default
        };

        if (editingAddress.id) {
            await supabase.from('addresses').update(addrData).eq('id', editingAddress.id);
        } else {
            await supabase.from('addresses').insert([addrData]);
        }
        setEditingAddress(null);
        fetchAddresses();
        setMessage('Dirección guardada con éxito');
        setTimeout(() => setMessage(''), 3000);
    };

    const deleteAddress = async (id: string) => {
        await supabase.from('addresses').delete().eq('id', id);
        fetchAddresses();
    };

    const fetchMyOrders = async () => {
        setIsLoading(true);
        try {
            const user = session?.user;
            if (!user) return;

            const { data, error } = await supabase
                .from('pedidos')
                .select('*')
                .eq('cliente_email', user.email)
                .order('created_at', { ascending: false });

            if (error) throw error;
            setOrders(data || []);
        } catch (e: any) {
            console.error("Error fetching orders:", e.message);
        } finally {
            setIsLoading(false);
        }
    };

    const handleLogout = async () => {
        await supabase.auth.signOut();
        onNavigate('home');
    };

    return (
        <div className="pt-24 min-h-screen bg-mabelita-light pb-12">
            <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
                <h1 className="text-4xl font-serif text-mabelita-dark mb-8">Mi Cuenta</h1>

                <div className="flex flex-col md:flex-row gap-8">
                    {/* Sidebar */}
                    <div className="w-full md:w-64 flex-shrink-0">
                        <div className="bg-white rounded-2xl shadow-sm border border-gray-100 overflow-hidden">
                            <nav className="flex flex-col">
                                <button
                                    onClick={() => setActiveTab('perfil')}
                                    className={`flex items-center gap-3 px-6 py-4 text-sm font-bold transition-colors ${activeTab === 'perfil' ? 'bg-mabelita-primary text-white' : 'text-gray-600 hover:bg-gray-50'}`}
                                >
                                    <User size={18} />
                                    Mi Perfil
                                </button>
                                <button
                                    onClick={() => setActiveTab('pedidos')}
                                    className={`flex items-center gap-3 px-6 py-4 text-sm font-bold transition-colors ${activeTab === 'pedidos' ? 'bg-mabelita-primary text-white' : 'text-gray-600 hover:bg-gray-50'}`}
                                >
                                    <Package size={18} />
                                    Historial de Pedidos
                                </button>
                                <button
                                    onClick={() => setActiveTab('direcciones')}
                                    className={`flex items-center gap-3 px-6 py-4 text-sm font-bold transition-colors ${activeTab === 'direcciones' ? 'bg-mabelita-primary text-white' : 'text-gray-600 hover:bg-gray-50'}`}
                                >
                                    <MapPin size={18} />
                                    Mis Direcciones
                                </button>
                                <div className="border-t border-gray-100 mt-2"></div>
                                <button
                                    onClick={handleLogout}
                                    className="flex items-center gap-3 px-6 py-4 text-sm font-bold text-red-600 hover:bg-red-50 transition-colors"
                                >
                                    <LogOut size={18} />
                                    Cerrar Sesión
                                </button>
                            </nav>
                        </div>
                    </div>

                    {/* Main Content */}
                    <div className="flex-grow">
                        <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-6 md:p-8 min-h-[500px]">

                            {/* TAB: PERFIL */}
                            {activeTab === 'perfil' && (
                                <div className="animate-fade-in">
                                    <h2 className="text-2xl font-serif font-bold text-mabelita-dark mb-6">Información Personal</h2>
                                    {message && <div className="mb-4 p-3 bg-green-50 text-green-700 rounded-lg flex items-center gap-2"><CheckCircle size={16} /> {message}</div>}
                                    <div className="space-y-4 max-w-lg">
                                        <div>
                                            <label className="block text-xs font-bold text-gray-500 uppercase mb-1">Nombre Completo</label>
                                            <input
                                                type="text"
                                                value={profile.full_name}
                                                onChange={e => setProfile({ ...profile, full_name: e.target.value })}
                                                className="w-full p-3 border border-gray-200 rounded-lg focus:ring-2 focus:ring-mabelita-primary outline-none"
                                            />
                                        </div>
                                        <div>
                                            <label className="block text-xs font-bold text-gray-500 uppercase mb-1">Teléfono</label>
                                            <input
                                                type="text"
                                                value={profile.phone}
                                                onChange={e => setProfile({ ...profile, phone: e.target.value })}
                                                className="w-full p-3 border border-gray-200 rounded-lg focus:ring-2 focus:ring-mabelita-primary outline-none"
                                            />
                                        </div>
                                        <div>
                                            <label className="block text-xs font-bold text-gray-500 uppercase mb-1">Email</label>
                                            <input
                                                type="text"
                                                readOnly
                                                value={session?.user?.email || ''}
                                                className="w-full p-3 border border-gray-200 rounded-lg bg-gray-50 text-gray-600"
                                            />
                                            <p className="text-xs text-gray-400 mt-2">El email no puede modificarse ya que es tu identificador de acceso único.</p>
                                        </div>
                                        <button onClick={saveProfile} className="px-6 py-3 bg-mabelita-primary text-white font-bold rounded-lg shadow-md hover:opacity-90 transition-opacity mt-4">Guardar Cambios</button>
                                    </div>
                                </div>
                            )}

                            {/* TAB: PEDIDOS */}
                            {activeTab === 'pedidos' && (
                                <div className="animate-fade-in">
                                    <h2 className="text-2xl font-serif font-bold text-mabelita-dark mb-6">Mis Pedidos</h2>
                                    {isLoading ? (
                                        <div className="text-center py-12 text-gray-500">Cargando pedidos...</div>
                                    ) : orders.length === 0 ? (
                                        <div className="text-center py-12 bg-gray-50 rounded-xl border border-dashed border-gray-200">
                                            <Package size={48} className="mx-auto text-gray-300 mb-4" />
                                            <p className="text-gray-500">Aún no has realizado ningún pedido.</p>
                                            <button
                                                onClick={() => onNavigate('tienda')}
                                                className="mt-4 px-6 py-2 bg-mabelita-primary text-white rounded-full font-bold hover:opacity-90 transition-opacity"
                                            >
                                                Ir de Compras
                                            </button>
                                        </div>
                                    ) : (
                                        <div className="space-y-4">
                                            {orders.map((order) => (
                                                <div key={order.id} className="border border-gray-100 rounded-xl p-6 hover:shadow-md transition-shadow">
                                                    <div className="flex flex-col md:flex-row justify-between items-start md:items-center mb-4 pb-4 border-b border-gray-100">
                                                        <div>
                                                            <span className="text-xs font-bold text-gray-500 uppercase">Pedido #{order.id}</span>
                                                            <div className="text-sm text-gray-600 mt-1">{new Date(order.created_at).toLocaleDateString()}</div>
                                                        </div>
                                                        <div className="mt-2 md:mt-0 text-right">
                                                            <span className="text-lg font-bold text-mabelita-dark">€{Number(order.total).toFixed(2)}</span>
                                                        </div>
                                                    </div>

                                                    <div className="flex justify-between items-center">
                                                        <span className={`px-3 py-1 rounded-full text-xs font-bold uppercase ${order.estado === 'completado' ? 'bg-green-100 text-green-700' :
                                                            order.estado === 'enviado' ? 'bg-blue-100 text-blue-700' :
                                                                order.estado === 'pagado' ? 'bg-yellow-100 text-yellow-700' :
                                                                    'bg-gray-100 text-gray-700'
                                                            }`}>
                                                            {order.estado || 'Procesando'}
                                                        </span>
                                                    </div>
                                                </div>
                                            ))}
                                        </div>
                                    )}
                                </div>
                            )}

                            {/* TAB: DIRECCIONES */}
                            {activeTab === 'direcciones' && (
                                <div className="animate-fade-in">
                                    <div className="flex justify-between items-center mb-6">
                                        <h2 className="text-2xl font-serif font-bold text-mabelita-dark">Mis Direcciones</h2>
                                        {!editingAddress && (
                                            <button onClick={() => setEditingAddress({ full_name: '', line1: '', city: '', postal_code: '', country: 'España', phone: '', is_default: false })} className="flex items-center gap-2 px-4 py-2 bg-mabelita-primary text-white rounded-lg text-sm font-bold">
                                                <Plus size={16} /> Nueva Dirección
                                            </button>
                                        )}
                                    </div>

                                    {message && <div className="mb-4 p-3 bg-green-50 text-green-700 rounded-lg flex items-center gap-2"><CheckCircle size={16} /> {message}</div>}

                                    {editingAddress ? (
                                        <form onSubmit={saveAddress} className="bg-gray-50 p-6 rounded-xl border border-gray-200">
                                            <h3 className="font-bold mb-4">{editingAddress.id ? 'Editar Dirección' : 'Añadir Dirección'}</h3>
                                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                                <div className="md:col-span-2">
                                                    <label className="block text-xs font-bold text-gray-500 mb-1">Nombre Completo</label>
                                                    <input required type="text" value={editingAddress.full_name} onChange={e => setEditingAddress({ ...editingAddress, full_name: e.target.value })} className="w-full p-3 border rounded-lg" />
                                                </div>
                                                <div className="md:col-span-2">
                                                    <label className="block text-xs font-bold text-gray-500 mb-1">Dirección (Calle, número)</label>
                                                    <input required type="text" value={editingAddress.line1} onChange={e => setEditingAddress({ ...editingAddress, line1: e.target.value })} className="w-full p-3 border rounded-lg" />
                                                </div>
                                                <div>
                                                    <label className="block text-xs font-bold text-gray-500 mb-1">Ciudad</label>
                                                    <input required type="text" value={editingAddress.city} onChange={e => setEditingAddress({ ...editingAddress, city: e.target.value })} className="w-full p-3 border rounded-lg" />
                                                </div>
                                                <div>
                                                    <label className="block text-xs font-bold text-gray-500 mb-1">Código Postal</label>
                                                    <input required type="text" value={editingAddress.postal_code} onChange={e => setEditingAddress({ ...editingAddress, postal_code: e.target.value })} className="w-full p-3 border rounded-lg" />
                                                </div>
                                                <div>
                                                    <label className="block text-xs font-bold text-gray-500 mb-1">País</label>
                                                    <select value={editingAddress.country} onChange={e => setEditingAddress({ ...editingAddress, country: e.target.value })} className="w-full p-3 border rounded-lg bg-white">
                                                        <option value="España">España</option>
                                                        <option value="Europa">Europa (UE)</option>
                                                        <option value="Internacional">Internacional (Resto del Mundo)</option>
                                                    </select>
                                                </div>
                                                <div>
                                                    <label className="block text-xs font-bold text-gray-500 mb-1">Teléfono</label>
                                                    <input required type="text" value={editingAddress.phone} onChange={e => setEditingAddress({ ...editingAddress, phone: e.target.value })} className="w-full p-3 border rounded-lg" />
                                                </div>
                                                <div className="md:col-span-2 flex items-center gap-2 mt-2">
                                                    <input type="checkbox" id="is_default" checked={editingAddress.is_default} onChange={e => setEditingAddress({ ...editingAddress, is_default: e.target.checked })} className="w-4 h-4 text-mabelita-primary" />
                                                    <label htmlFor="is_default" className="text-sm">Establecer como dirección predeterminada</label>
                                                </div>
                                            </div>
                                            <div className="mt-6 flex justify-end gap-3">
                                                <button type="button" onClick={() => setEditingAddress(null)} className="px-5 py-2 text-gray-600 hover:bg-gray-100 rounded-lg">Cancelar</button>
                                                <button type="submit" className="px-5 py-2 bg-mabelita-dark text-white rounded-lg font-bold">Guardar Dirección</button>
                                            </div>
                                        </form>
                                    ) : (
                                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                            {addresses.length === 0 ? (
                                                <div className="md:col-span-2 text-center py-12 bg-gray-50 rounded-xl border border-dashed border-gray-200">
                                                    <MapPin size={48} className="mx-auto text-gray-300 mb-4" />
                                                    <p className="text-gray-500">Aún no tienes direcciones guardadas.</p>
                                                </div>
                                            ) : (
                                                addresses.map(addr => (
                                                    <div key={addr.id} className={`p-5 rounded-xl border relative ${addr.is_default ? 'border-mabelita-primary bg-mabelita-primary/5' : 'border-gray-200 bg-white'}`}>
                                                        {addr.is_default && <span className="absolute top-4 right-4 text-xs font-bold text-mabelita-primary bg-white px-2 py-1 rounded-md shadow-sm">Predeterminada</span>}
                                                        <h3 className="font-bold text-lg mb-1 pr-20">{addr.full_name}</h3>
                                                        <p className="text-sm text-gray-600 mb-1">{addr.line1}</p>
                                                        <p className="text-sm text-gray-600 mb-1">{addr.city}, {addr.postal_code}</p>
                                                        <p className="text-sm text-gray-600 mb-3">{addr.country}</p>
                                                        <p className="text-sm text-gray-500 mb-4 flex items-center gap-1">📞 {addr.phone}</p>

                                                        <div className="flex gap-3 border-t border-gray-100 pt-4">
                                                            <button onClick={() => setEditingAddress(addr)} className="text-sm font-bold text-blue-600 hover:text-blue-800 flex items-center gap-1"><Edit2 size={14} /> Editar</button>
                                                            <button onClick={() => deleteAddress(addr.id)} className="text-sm font-bold text-red-600 hover:text-red-800 flex items-center gap-1"><Trash2 size={14} /> Eliminar</button>
                                                        </div>
                                                    </div>
                                                ))
                                            )}
                                        </div>
                                    )}
                                </div>
                            )}

                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};
